/**
 * THE CONTROLS ABOVE A LIST OF REVIEWS — the search field, the "Sort by" and
 * "Rating" chips, the two menus they open, and (separately) the star
 * histogram. One copy, worn by the product reviews sheet and by the shop's
 * merged reviews view.
 *
 * It was measured against `shop.app/m/johnelliott` for the product sheet and
 * is unchanged here, down to the DOM: the same elements in the same order
 * with the same classes, and every `data-testid` built from a prefix the
 * caller supplies (`reviews-modal`, `shop-reviews-modal`). That is what lets
 * the product sheet keep its suite green while a second list reuses it.
 *
 * TWO THINGS THAT LOOK LIKE STYLE AND ARE NOT:
 *
 * 1. The open menu is a SIBLING of the chip row, not a child. The row is
 *    `overflow-x: auto` for the chips that do not fit, and a scroll
 *    container clips an absolutely-positioned child on BOTH axes —
 *    `overflow-y: visible` beside `overflow-x: auto` computes to `auto`. The
 *    first build's menus opened into a clipping box and were invisible on
 *    the live page.
 * 2. Class names avoid the substrings `panel`, `popup`, `overlay` and
 *    `modal`+`box`/`content`: `src/modal-fixes.css` matches those anywhere
 *    inside a `[role="dialog"]` and applies `overscroll-behavior: contain`,
 *    which on a scroll container with nothing to scroll swallows the wheel.
 */
import React, { useEffect, useRef, useState } from 'react';
import { FiChevronDown, FiSearch } from 'react-icons/fi';
import { StarsRow } from './ReviewCard';
import styles from './reviewsModal.module.css';

const ReviewsFilterBar = ({
  t,
  testIdPrefix,
  query, onQuery, searchAria,
  sort, onSort, sortOptions, defaultSort,
  stars, onToggleStar, onResetStars,
  histogram,
  filtersActive, onClearAll,
  children,
}) => {
  const [openMenu, setOpenMenu] = useState(null); // 'sort' | 'rating' | null
  const menuRef = useRef(null);

  // Close the open menu on outside click / Escape.
  useEffect(() => {
    if (!openMenu) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setOpenMenu(null); };
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpenMenu(null); } };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [openMenu]);

  const activeSort = sortOptions.find((s) => s.value === sort) || sortOptions[0];
  const sortLabel = t(activeSort.key, { defaultValue: activeSort.label });

  return (
    <>
      {/* Search. A real <input type="search"> so the platform gives the
          clear affordance and the right on-screen keyboard. */}
      <div className={styles.searchRow}>
        <FiSearch className={styles.searchIcon} aria-hidden />
        <input
          type="search"
          className={styles.searchInput}
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder={t('reviews.searchPlaceholder', { defaultValue: 'Search' })}
          aria-label={searchAria}
          data-testid={`${testIdPrefix}-search`}
        />
      </div>

      <div className={styles.controls} ref={menuRef}>
        <div className={styles.chipRow} data-testid={`${testIdPrefix}-controls`}>
          <div className={styles.chipWrap}>
            <button
              type="button"
              className={`${styles.chip} ${sort !== defaultSort ? styles.chipOn : ''}`}
              onClick={() => setOpenMenu((m) => (m === 'sort' ? null : 'sort'))}
              aria-haspopup="listbox"
              aria-expanded={openMenu === 'sort'}
              aria-label={t('reviews.sortLabel', { defaultValue: 'Sort by' })}
              data-testid={`${testIdPrefix}-sort`}
            >
              <span>{sort === defaultSort ? t('reviews.sortLabel', { defaultValue: 'Sort by' }) : sortLabel}</span>
              <FiChevronDown className={styles.chipChevron} aria-hidden />
            </button>
          </div>

          <div className={styles.chipWrap}>
            <button
              type="button"
              className={`${styles.chip} ${stars.size > 0 ? styles.chipOn : ''}`}
              onClick={() => setOpenMenu((m) => (m === 'rating' ? null : 'rating'))}
              aria-haspopup="true"
              aria-expanded={openMenu === 'rating'}
              aria-label={t('reviews.ratingLabel', { defaultValue: 'Rating' })}
              data-testid={`${testIdPrefix}-rating`}
            >
              <span>{t('reviews.ratingLabel', { defaultValue: 'Rating' })}</span>
              {stars.size > 0 && <span className={styles.chipCount}>{stars.size}</span>}
              <FiChevronDown className={styles.chipChevron} aria-hidden />
            </button>
          </div>

          {/* The caller's own chips — With photos and the variants on the
              product sheet, nothing on the shop's (its sources get a row of
              their own, above the search, because they are the first
              question that list answers). */}
          {children}

          {filtersActive && (
            <button
              type="button"
              className={styles.chip}
              onClick={onClearAll}
              data-testid={`${testIdPrefix}-filter-all`}
            >
              {t('reviews.clearFilters', { defaultValue: 'Clear' })}
            </button>
          )}
        </div>

        {openMenu === 'sort' && (
          <div className={styles.menuCard} data-testid={`${testIdPrefix}-sort-menu`}>
            <h3 className={styles.menuTitle}>{t('reviews.sortLabel', { defaultValue: 'Sort by' })}</h3>
            <ul className={styles.menuList} role="listbox" aria-label={t('reviews.sortLabel', { defaultValue: 'Sort by' })}>
              {sortOptions.map((o) => (
                <li key={o.value}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={sort === o.value}
                    className={`${styles.menuRow} ${sort === o.value ? styles.menuRowOn : ''}`}
                    onClick={() => { onSort(o.value); setOpenMenu(null); }}
                    data-testid={`${testIdPrefix}-sort-${o.value}`}
                  >
                    <span className={styles.menuRowLabel}>{t(o.key, { defaultValue: o.label })}</span>
                    <span className={styles.radio} aria-hidden />
                  </button>
                </li>
              ))}
            </ul>
            <div className={styles.menuFoot}>
              <button
                type="button" className={styles.menuReset}
                onClick={() => onSort(defaultSort)}
                data-testid={`${testIdPrefix}-sort-reset`}
              >
                {t('reviews.reset', { defaultValue: 'Reset' })}
              </button>
              <button
                type="button" className={styles.menuDone}
                onClick={() => setOpenMenu(null)}
                data-testid={`${testIdPrefix}-sort-done`}
              >
                {t('reviews.done', { defaultValue: 'Done' })}
              </button>
            </div>
          </div>
        )}

        {openMenu === 'rating' && (
          <div className={styles.menuCard} data-testid={`${testIdPrefix}-rating-menu`}>
            <h3 className={styles.menuTitle}>{t('reviews.ratingLabel', { defaultValue: 'Rating' })}</h3>
            <div className={styles.menuList} role="group" aria-label={t('reviews.ratingLabel', { defaultValue: 'Rating' })}>
              {[5, 4, 3, 2, 1].map((s) => {
                const on = stars.has(s);
                return (
                  <button
                    type="button"
                    key={s}
                    role="checkbox"
                    aria-checked={on}
                    aria-label={t('reviews.filterByStars', { defaultValue: 'Show {{n}}-star reviews', n: s })}
                    className={`${styles.menuRow} ${on ? styles.menuRowOn : ''}`}
                    onClick={() => onToggleStar(s)}
                    data-testid={`${testIdPrefix}-rating-${s}`}
                  >
                    <span className={styles.menuRowLabel}>
                      <StarsRow value={s} size={16} t={t} />
                      <span className={styles.menuRowNum}>{s}</span>
                      <span className={styles.menuRowCount}>{histogram?.[s] ?? 0}</span>
                    </span>
                    <span className={styles.checkbox} aria-hidden />
                  </button>
                );
              })}
            </div>
            <div className={styles.menuFoot}>
              <button
                type="button" className={styles.menuReset}
                onClick={onResetStars}
                data-testid={`${testIdPrefix}-rating-reset`}
              >
                {t('reviews.reset', { defaultValue: 'Reset' })}
              </button>
              <button
                type="button" className={styles.menuDone}
                onClick={() => setOpenMenu(null)}
                data-testid={`${testIdPrefix}-rating-done`}
              >
                {t('reviews.done', { defaultValue: 'Done' })}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

/**
 * THE DISTRIBUTION. Not in the reference, but it answers "is 3.3 one bad
 * review or twenty" at a glance, and each row drives the SAME set of ticked
 * stars the Rating menu does — so the two controls can never disagree.
 */
export const ReviewsHistogram = ({ histogram, stars, onToggleStar, t, testIdPrefix }) => {
  const maxBar = Math.max(1, ...Object.values(histogram || {}).map((n) => Number(n) || 0));
  return (
    <div className={styles.histogram} data-testid={`${testIdPrefix}-histogram`}>
      {[5, 4, 3, 2, 1].map((s) => {
        const n = Number(histogram?.[s]) || 0;
        const w = Math.round((n / maxBar) * 100);
        const active = stars.has(s);
        return (
          <button
            type="button"
            key={s}
            className={`${styles.histRow} ${active ? styles.histRowOn : ''}`}
            onClick={() => onToggleStar(s)}
            disabled={n === 0}
            aria-pressed={active}
            aria-label={t('reviews.filterByStars', { defaultValue: 'Show {{n}}-star reviews', n: s })}
            data-testid={`${testIdPrefix}-star-filter-${s}`}
          >
            <span className={styles.histLabel}>
              {s}
              <StarsRow value={1} size={12} count={1} t={t} />
            </span>
            <span className={styles.histBarBg}>
              <span className={styles.histBarFg} style={{ inlineSize: `${w}%` }} />
            </span>
            <span className={styles.histCount}>{n}</span>
          </button>
        );
      })}
    </div>
  );
};

export default ReviewsFilterBar;
